import type { User } from '@datadog/browser-core';
import { datadogRum } from '@datadog/browser-rum';

import { getRegion } from '@adsk/acs-sc-web-platform-api';
import { createMonitoringListener, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import { setDynatraceUser } from './dynatrace';
import { sendEventToDynatrace } from './dynatraceReport';
import { hasOverrides, getRemoteOverrides } from './getRemoteOverrides';

const CURRENT_TOOL_SCOPE_KEY = 'exo-current-tool-scope';
const VISIBLE_REMOTES_METADATA_KEY = 'exo-visible-remotes-metadata';
const VERSION_KEY = 'exo-version';
const BUILD_IDENTIFIER_KEY = 'exo-build-identifier';

let monitoringInitialized = false;
let monitoringListener:
  | {
      dispose(): void;
    }
  | undefined;

export interface MetadataType {
  [key: string]: unknown;
  scope?: string;
}

function reportError(
  error: Error | string,
  metadata: MetadataType,
  scope?: string,
) {
  if (!monitoringInitialized) {
    throw new Error(
      'Monitoring is not initialized. Please call initializeMonitoring() before calling reportError()',
    );
  }

  error = typeof error === 'string' ? new Error(error) : error;

  datadogRum.addError(error, { stack: error?.stack, ...(metadata ?? {}) });

  sendEventToDynatrace({
    data: error,
    scope,
    metadata,
    isError: metadata.diagnosticType === 'warning' ? false : true, // we can revisit diagnosticType once remove datadog
  });
}

function reportWarning(
  error: Error | string,
  metadata: MetadataType,
  scope?: string,
) {
  reportError(error, { diagnosticType: 'warning', ...metadata }, scope);

  console.warn(error);
}

export function setUserToErrorContext(
  user: User & { analyticsId?: string | null },
) {
  datadogRum.setUser(user);
  datadogRum.setGlobalContextProperty('hasUser', true);

  if (user.analyticsId) {
    setDynatraceUser(user.analyticsId);
    datadogRum.setGlobalContextProperty('analyticsId', user.analyticsId);
  }
}

export function setOverrideToErrorContext() {
  if (hasOverrides()) {
    datadogRum.setGlobalContextProperty(
      'overrideDetails',
      getRemoteOverrides(),
    );
  }
}

export function removeUserFromErrorContext() {
  datadogRum.clearUser();
  datadogRum.removeGlobalContextProperty('hasUser');
  datadogRum.removeGlobalContextProperty('analyticsId');
}

export function disposeMonitoringContext() {
  if (!monitoringInitialized) {
    return;
  }

  removeUserFromErrorContext();
}

export function setCurrentToolScopeToContext(scope: string) {
  datadogRum.setGlobalContextProperty(CURRENT_TOOL_SCOPE_KEY, scope);
}

export function removeCurrentToolScopeFromContext() {
  datadogRum.removeGlobalContextProperty(CURRENT_TOOL_SCOPE_KEY);
}

let visibleRemotes: Record<string, Record<string, { visible: boolean }>> = {};

export function setVisibleRemoteContext(scope: string, moduleName: string) {
  if (!visibleRemotes[scope]) {
    visibleRemotes[scope] = {};
  }

  visibleRemotes[scope][moduleName] = { visible: true };

  datadogRum.setGlobalContextProperty(VISIBLE_REMOTES_METADATA_KEY, {
    ...visibleRemotes,
  });
}

export function removeVisibleRemoteContext(scope: string, moduleName: string) {
  if (!visibleRemotes[scope]) {
    return;
  }

  delete visibleRemotes[scope][moduleName];

  if (Object.keys(visibleRemotes[scope]).length === 0) {
    delete visibleRemotes[scope];
  }

  datadogRum.setGlobalContextProperty(VISIBLE_REMOTES_METADATA_KEY, {
    ...visibleRemotes,
  });
}

export function setProjectToContext({
  projectId,
  projectName,
}: {
  projectId: string;
  projectName?: string | null;
}) {
  datadogRum.setGlobalContextProperty('projectId', projectId);

  if (projectName) {
    datadogRum.setGlobalContextProperty('projectName', projectName);
  }
}

export function removeProjectFromContext() {
  datadogRum.removeGlobalContextProperty('projectId');
  datadogRum.removeGlobalContextProperty('projectName');
}

export function setAccountToContext({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName?: string | null;
}) {
  datadogRum.setGlobalContextProperty('accountId', accountId);
  datadogRum.setGlobalContextProperty('accountName', accountName);
}

export function removeAccountFromContext() {
  datadogRum.removeGlobalContextProperty('accountId');
  datadogRum.removeGlobalContextProperty('accountName');
}

export function resetVisibleRemoteContext() {
  visibleRemotes = {};
  datadogRum.removeGlobalContextProperty(VISIBLE_REMOTES_METADATA_KEY);
}

export function disposeMonitoring() {
  if (monitoringInitialized) {
    monitoringInitialized = false;

    if (monitoringListener) {
      monitoringListener.dispose();
    }
  }
}

export function initializeMonitoringHandler() {
  if (!monitoringInitialized) {
    monitoringInitialized = true;

    monitoringListener = createMonitoringListener({
      onReport({ data: { type, value, context, scope } }) {
        switch (type) {
          case REPORT_TYPES.error:
            reportError(value, context, scope);
            break;
          case REPORT_TYPES.warning:
            reportWarning(value, context, scope);
            break;
          case REPORT_TYPES.info:
            datadogRum.addAction(typeof value === 'string' ? value : 'info', {
              value,
              ...(context || {}),
            });

            sendEventToDynatrace({
              data:
                typeof value === 'string'
                  ? `INFO - ${value}`
                  : `INFO - ${value.message}`,
              isError: false,
              metadata: context,
              scope,
            });
            break;
          default:
            break;
        }
      },
    });
  }
}

export function initializeMonitoringMetadata() {
  datadogRum.setGlobalContextProperty('region', getRegion());

  const version = process.env.APPLICATION_VERSION;
  const buildIdentifier = process.env.APPLICATION_UNIQUE_BUILD_ID;

  if (!version) {
    throw new Error(
      'APPLICATION_VERSION is not defined. Please check your webpack config.',
    );
  }

  if (!buildIdentifier) {
    throw new Error(
      'APPLICATION_UNIQUE_BUILD_ID is not defined. Please check your webpack config.',
    );
  }

  datadogRum.setGlobalContextProperty(VERSION_KEY, version);
  datadogRum.setGlobalContextProperty(BUILD_IDENTIFIER_KEY, buildIdentifier);
}

export const REPORT_EVENT_TYPES = {
  REMOTE_MODULE_ERROR: 'remote-module-error',
  ROOT_ERROR: 'root-error-boundary',
  PAGE_NOT_FOUND: 'PAGE_NOT_FOUND',
  DEPRECATED_ROUTE_OR_REDIRECT: 'DEPRECATED_ROUTE_OR_REDIRECT',
};
